import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, RefreshCw, Pencil, Eye, ChevronDown, FileText } from 'lucide-react';
import { Layout } from '../components/layout';
import { Button, ConfirmDialog } from '../components/ui';
import {
  PdfViewer,
  TranslatedContentViewer,
  CaseInfoCard,
  CaseActionBar,
  CaseHistoryPanel,
  SalesforceStatusPanel,
  DiffModal,
} from '../components/review';
import { ReviewTourPrompt } from '../components/onboarding';
import { useCaseReview } from '../hooks/useCaseReview';
import {
  hasTranslation as checkHasTranslation,
  hasPdf as checkHasPdf,
  isApproved as checkIsApproved,
  canApprove as checkCanApprove,
  isInReview as checkIsInReview,
} from '../utils/caseStatus';

// Pop out icon
const PopOutIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-gray-500">
    <path d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14z"/>
  </svg>
);

export function CaseReviewPage() {
  const { caseNumber } = useParams<{ caseNumber: string }>();

  const {
    // Case data
    currentCase,
    prevCaseNumber,
    nextCaseNumber,
    isLoading,
    caseNotFound,

    // PDF state
    pdfUrl,
    pdfLoading,
    translatedPdfUrl,
    translatedPdfLoading,

    // HTML state
    htmlContent,
    htmlLoading,
    htmlError,

    // View mode
    viewMode,
    setViewMode,

    // Status flags
    isCaseTranslating,
    isRestarting,
    isGeneratingPdf,

    // History panel
    historyOpen,
    historyData,
    historyLoading,
    tokenUsageData,
    tokenUsageLoading,

    // Diff modal
    diffModalOpen,
    selectedDiff,

    // SF status panel
    sfStatusOpen,
    sfStatusData,
    sfStatusLoading,

    // Restart confirmation
    restartConfirmOpen,

    // Actions
    clearPanels,
    handleTranslate,
    handleApprove,
    handleUnapprove,
    handleStatusChange,
    handleRestartClick,
    handleRestartConfirm,
    handleRestartCancel,
    handleToggleHistory,
    handleGetSfStatus,
    setHistoryOpen,
    setSfStatusOpen,
    handlePopOutPDF,
    handlePopOutTranslation,
    handleSaveHtml,
    handleRegeneratePdf,
    handleDownloadWord,
    handleDownloadHtml,
    handleViewDiff,
    handleCloseDiffModal,
  } = useCaseReview({ caseNumber });

  // Download dropdown state (local to this component)
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target as Node)) {
        setDownloadDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show loading while fetching case
  if (isLoading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <span className="inline-block w-8 h-8 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Loading case data...</p>
        </div>
      </Layout>
    );
  }

  if (caseNotFound || !currentCase) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Case Not Found</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">Case {caseNumber} could not be found.</p>
          <Link to="/">
            <Button variant="primary">Back to Dashboard</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  // Computed status checks
  const isApproved = checkIsApproved(currentCase.status);
  const canApprove = checkCanApprove(currentCase.status);
  const hasTranslation = checkHasTranslation(currentCase.status);
  const hasPdf = checkHasPdf(currentCase.status);
  const isInReview = checkIsInReview(currentCase.status);

  const handleDownloadWordClick = () => {
    setDownloadDropdownOpen(false);
    handleDownloadWord();
  };

  const handleDownloadHtmlClick = () => {
    setDownloadDropdownOpen(false);
    handleDownloadHtml();
  };

  return (
    <Layout>
      {/* Case Info Card */}
      <div className="bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg shadow-sm mb-4">
        {/* Top row: Navigation arrows + Case number + Metadata */}
        <CaseInfoCard
          currentCase={currentCase}
          caseNumber={caseNumber!}
          prevCaseNumber={prevCaseNumber}
          nextCaseNumber={nextCaseNumber}
          onNavigate={clearPanels}
        />

        {/* Bottom row: Status dropdown + Actions */}
        <CaseActionBar
          currentCase={currentCase}
          hasTranslation={hasTranslation}
          isCaseTranslating={isCaseTranslating}
          isRestarting={isRestarting}
          isApproved={isApproved}
          canApprove={canApprove}
          sfStatusLoading={sfStatusLoading}
          onTranslate={handleTranslate}
          onApprove={handleApprove}
          onUnapprove={handleUnapprove}
          onRestart={handleRestartClick}
          onToggleHistory={handleToggleHistory}
          onGetSfStatus={handleGetSfStatus}
          onStatusChange={handleStatusChange}
        />
      </div>

      {/* Case History Panel */}
      {historyOpen && (
        <CaseHistoryPanel
          historyData={historyData}
          historyLoading={historyLoading}
          tokenUsageData={tokenUsageData}
          tokenUsageLoading={tokenUsageLoading}
          onClose={() => setHistoryOpen(false)}
          onViewDiff={handleViewDiff}
        />
      )}

      {/* SF Status Panel */}
      {sfStatusOpen && (
        <SalesforceStatusPanel
          isLoading={sfStatusLoading}
          statusData={sfStatusData}
          onClose={() => setSfStatusOpen(false)}
        />
      )}

      {/* Two-pane review container */}
      <div className="grid grid-cols-2 gap-4 h-[calc(100vh-120px)]">
        {/* Original Document Pane */}
        <div className="flex flex-col bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg shadow-sm overflow-hidden" data-onboarding="original-pane">
          <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100 dark:border-dark-border">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">Original Document</h3>
            <button onClick={handlePopOutPDF} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Pop out viewer">
              <PopOutIcon />
            </button>
          </div>
          <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-dark-recessed">
            <PdfViewer url={pdfUrl} isLoading={pdfLoading} title="Original Document" />
          </div>
        </div>

        {/* Translated Document Pane */}
        <div className="flex flex-col bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg shadow-sm overflow-hidden" data-onboarding="translated-pane">
          <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100 dark:border-dark-border">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">Translated Document</h3>
              {hasTranslation && (
                <button onClick={handlePopOutTranslation} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" title="Pop out viewer">
                  <PopOutIcon />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasTranslation && (
                <>
                  {isInReview && (
                    <>
                      <Button variant="ghost" size="sm" onClick={handleRegeneratePdf} disabled={isGeneratingPdf} isLoading={isGeneratingPdf} title="Regenerate PDF" data-onboarding="regen-pdf-btn">
                        <RefreshCw className="w-4 h-4 mr-1" />PDF
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setViewMode(viewMode === 'pdf' ? 'html' : 'pdf')} title={viewMode === 'pdf' ? 'Edit HTML' : 'View PDF'} data-onboarding="edit-btn">
                        {viewMode === 'pdf' ? <><Pencil className="w-4 h-4 mr-1" />Edit</> : <><Eye className="w-4 h-4 mr-1" />View</>}
                      </Button>
                    </>
                  )}
                  <div className="relative" ref={downloadDropdownRef} data-onboarding="export-btn">
                    <Button variant="ghost" size="sm" onClick={() => setDownloadDropdownOpen(!downloadDropdownOpen)}>
                      <Download className="w-4 h-4 mr-1" />Export<ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                    {downloadDropdownOpen && (
                      <div className="absolute right-0 mt-1 w-36 bg-white dark:bg-dark-elevated border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-10 py-1">
                        <button onClick={handleDownloadWordClick} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-hover flex items-center gap-2 text-gray-700 dark:text-gray-300" disabled={!hasPdf}>
                          <FileText className="w-4 h-4" />Word
                        </button>
                        <button onClick={handleDownloadHtmlClick} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-hover flex items-center gap-2 text-gray-700 dark:text-gray-300" disabled={!htmlContent}>
                          <FileText className="w-4 h-4" />HTML
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-dark-recessed">
            <TranslatedContentViewer
              isCaseTranslating={isCaseTranslating}
              isGeneratingPdf={isGeneratingPdf}
              hasTranslation={hasTranslation}
              hasPdf={hasPdf}
              viewMode={viewMode}
              htmlLoading={htmlLoading}
              htmlError={htmlError}
              htmlContent={htmlContent}
              translatedPdfLoading={translatedPdfLoading}
              translatedPdfUrl={translatedPdfUrl}
              isApproved={isApproved}
              onSaveHtml={handleSaveHtml}
              onSetViewMode={setViewMode}
            />
          </div>
        </div>
      </div>

      {/* Restart Confirmation Dialog */}
      <ConfirmDialog
        isOpen={restartConfirmOpen}
        title="Confirm Restart"
        message={
          <div>
            <p className="mb-2">
              Are you sure you want to restart case <strong>{caseNumber}</strong>?
            </p>
            <p className="text-red-600 font-medium">
              This will delete all files and re-download from Salesforce. Case history will be preserved.
            </p>
          </div>
        }
        confirmLabel="Restart"
        confirmVariant="danger"
        onConfirm={handleRestartConfirm}
        onCancel={handleRestartCancel}
      />

      {/* Diff Modal */}
      <DiffModal
        isOpen={diffModalOpen}
        diff={selectedDiff}
        onClose={handleCloseDiffModal}
      />

      {/* Review Tour Prompt - shows on first visit */}
      <ReviewTourPrompt />
    </Layout>
  );
}
